"use client";

import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { toggleJobAlerts } from "@/app/actions/listings";
import { trackEvent } from "@/lib/analytics";
import { buildWorkSignupHref } from "@/lib/signup-next";

const DISMISSED_KEY = "peakwave:job-alert-banner:dismissed";

interface JobAlertSignupProps {
  /** null = signed out, true/false = candidate pref state */
  candidateJobAlertsEnabled?: boolean | null;
  /** Where a signed-out visitor returns after work setup (a /jobs path). */
  signupNext?: string;
  className?: string;
}

export function JobAlertSignup({
  candidateJobAlertsEnabled = null,
  signupNext = "/jobs",
  className,
}: JobAlertSignupProps) {
  const isCandidate = candidateJobAlertsEnabled !== null;
  const [preference, setPreference] = useState({
    source: candidateJobAlertsEnabled,
    enabled: candidateJobAlertsEnabled === true,
  });
  const [loading, setLoading] = useState(false);
  const saving = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<"checking" | "visible" | "hidden">("checking");

  // Navigation and account settings can supply a newer saved preference.
  if (preference.source !== candidateJobAlertsEnabled) {
    setPreference({ source: candidateJobAlertsEnabled, enabled: candidateJobAlertsEnabled === true });
  }

  useEffect(() => {
    const timer = window.setTimeout(() => {
      try {
        setVisibility(window.localStorage.getItem(DISMISSED_KEY) === "true" ? "hidden" : "visible");
      } catch {
        setVisibility("visible");
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);

  function dismiss() {
    setVisibility("hidden");
    try {
      window.localStorage.setItem(DISMISSED_KEY, "true");
    } catch {
      // Hiding still works for this visit if browser storage is unavailable.
    }
  }

  async function handleCandidateToggle() {
    if (saving.current) return;
    saving.current = true;
    setLoading(true);
    setError(null);
    const enabled = !preference.enabled;
    try {
      const result = await toggleJobAlerts(enabled);
      if (!result.success || result.enabled !== enabled) {
        setError("Could not save your job alert setting. Please try again.");
        return;
      }
      setPreference({ source: candidateJobAlertsEnabled, enabled: result.enabled });
      trackEvent("job_alerts_toggled", { enabled: result.enabled });
    } catch {
      setError("Could not save your job alert setting. Please try again.");
    } finally {
      saving.current = false;
      setLoading(false);
    }
  }

  if (visibility !== "visible") return null;

  return (
    <aside aria-label="Job email alerts" className={className}>
      <div className="relative bg-navy rounded-[16px] pl-5 pr-14 py-4 sm:pl-6">
        <button
          type="button"
          aria-label="Hide job alerts banner"
          title="Hide this banner without changing your email settings"
          onClick={dismiss}
          className="absolute right-1 top-1 flex min-h-11 min-w-11 items-center justify-center rounded-full text-white/80 hover:bg-white/10 hover:text-white focus-visible:outline-2 focus-visible:outline-white"
        >
          <X size={20} aria-hidden="true" />
        </button>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          <div className="flex-1 min-w-0">
            <p role="status" className="text-white text-[14px] font-medium m-0">
              {isCandidate
                ? preference.enabled
                  ? "You will get an email when a new job matches your profile"
                  : "Job alerts are off"
                : "Create a free account to get matched job alerts"}
            </p>
            {error && <p role="alert" className="mt-2 text-[13px] text-white">{error}</p>}
          </div>
          {isCandidate ? (
            <div className="flex flex-wrap items-center gap-x-4 shrink-0">
              <button
                type="button"
                onClick={handleCandidateToggle}
                disabled={loading}
                className="min-h-11 text-[13px] font-semibold text-white underline underline-offset-4 hover:text-white/80 focus-visible:outline-2 focus-visible:outline-white disabled:opacity-50"
              >
                {loading ? "Saving..." : preference.enabled ? "Turn off" : "Turn on"}
              </button>
              <a href="/settings" className="inline-flex min-h-11 items-center text-[13px] text-white/80 underline underline-offset-4 hover:text-white focus-visible:outline-2 focus-visible:outline-white">
                Email settings
              </a>
            </div>
          ) : (
            <a
              href={buildWorkSignupHref({ next: signupNext, source: "job-alerts" })}
              className="inline-flex min-h-11 items-center justify-center px-5 py-2.5 rounded-sm text-[13px] font-semibold bg-gold text-white no-underline font-sans transition-all hover:bg-gold-light shrink-0 text-center"
            >
              Create free account
            </a>
          )}
        </div>
      </div>
    </aside>
  );
}
